import { isEmpty } from 'lodash';

import { processCoverageDecisionContent } from '~/components/Claim/Actions/CoverageDecision/CoverageDecisionActionProcessor';
import { processDamageAssessmentMarkdown } from '~/components/Claim/Actions/DamageAssessment/DamageAssessmentActionProcessor';
import { Action, ACTION_ICON_MAP, ActionType } from '~/components/Claim/Actions/types';
import ErrorIcon from '~/core/iconComponents/icons/ErrorIcon';
import OkIcon from '~/core/iconComponents/icons/OkIcon.tsx';
import { IconProps } from '~/core/iconComponents/icons/types.ts';
import { parseDateWithOptionalTimezone, toTimeAgo } from '~/utils/Dates/DateTimeUtils';

export const actionToListItem = (action: Action) => {
  const IconComponent = getActionIcon(action);

  const timeAgo = action.updated_at ? toTimeAgo(parseDateWithOptionalTimezone(action.updated_at)) : '';

  return {
    id: action.id,
    content: (
      <div className="flex w-full items-center pr-8">
        <div>{action.title}</div>
        <span className="ml-auto text-sm font-normal text-blue-950">{timeAgo}</span>
      </div>
    ),
    icon: (
      <div className="relative flex items-center justify-center rounded-lg bg-blue-100 p-4">
        <IconComponent size={16} className="text-blue-600" />
        {action.is_done ? <OkIcon size={30} className="absolute top-[-14px] right-[-18px] text-green-600" /> : null}
      </div>
    ),
  };
};

export const defaultActionProcessor = (action: Action): string => {
  const task_text = action?.metadata?.task_text as string;
  const explanation = action?.metadata?.explanation as string;
  return `${task_text}\n\n${explanation}`;
};

export const ACTION_PROCESSORS_MAP: Partial<Record<ActionType, (action: Action) => string>> = {
  assess_damage: processDamageAssessmentMarkdown,
  decide_on_coverage: processCoverageDecisionContent,
};

export const getActionIcon = (action: Action): React.ComponentType<IconProps> => {
  let type = action?.type;

  if (action.metadata?.icon_type) {
    type = action.metadata?.icon_type as ActionType;
  }

  if (isEmpty(type)) {
    return ErrorIcon;
  }

  return ACTION_ICON_MAP[type] || ErrorIcon;
};

export const sortActions = (actions: Action[] = []) => {
  return [...actions].sort((a, b) => {
    // Primary sort: is_done actions last (false before true)
    if (a.is_done !== b.is_done) {
      return a.is_done ? 1 : -1;
    }

    // Secondary sort: by created_at (most recent first)
    if (a.created_at && b.created_at) {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }

    // Handle cases where created_at might be undefined
    if (a.created_at && !b.created_at) return -1;
    if (!a.created_at && b.created_at) return 1;

    return 0;
  });
};
